import { FC, useCallback } from 'react';
import { Form, Formik } from 'formik';
import { useFragment, useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import LegacyEditor from 'components/LegacyEditor';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from 'components/TextField';

import { useDialog } from 'components/Dialog';
import {
  AddWhatToSeeBlockDialogMutation,
  AddWhatToSeeBlockDialogMutation$data,
} from '__generated__/AddWhatToSeeBlockDialogMutation.graphql';
import { useErrorHandler } from 'lib';
import { AddWhatToSeeBlockDialog_whatToSee$key } from '__generated__/AddWhatToSeeBlockDialog_whatToSee.graphql';

type FormFields = {
  name: string;
  description: string;
};

export type AddedBlock =
  AddWhatToSeeBlockDialogMutation$data['addBlockToWhatToSee']['node'];

type Props = {
  whatToSee: AddWhatToSeeBlockDialog_whatToSee$key;
  connectionId: string;
  onAdd: (block: AddedBlock) => void;
};

const initial = {
  name: '',
  description: '',
};

const AddWhatToSeeBlockDialog: FC<Props> = props => {
  const { connectionId, whatToSee, onAdd } = props;
  const { close } = useDialog();
  const errorHandler = useErrorHandler<AddWhatToSeeBlockDialogMutation$data>();

  const { id } = useFragment(
    graphql`
      fragment AddWhatToSeeBlockDialog_whatToSee on WhatToSee {
        id
      }
    `,
    whatToSee
  );

  const [update, updating] =
    useMutation<AddWhatToSeeBlockDialogMutation>(graphql`
      mutation AddWhatToSeeBlockDialogMutation(
        $id: ID!
        $input: CreateWhatToSeeBlockInput!
        $connections: [ID!]!
      ) {
        addBlockToWhatToSee(id: $id, input: $input) {
          node
            @appendNode(
              connections: $connections
              edgeTypeName: "WhatToSeeBlockEdge"
            ) {
            id
            name
            description
          }
        }
      }
    `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      update({
        variables: {
          id,
          input,
          connections: [connectionId],
        },

        ...errorHandler({
          onCompleted: data => {
            onAdd(data.addBlockToWhatToSee.node);
            close();
          },
        }),
      });
    },
    [close, connectionId, errorHandler, id, onAdd, update]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas blokas</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={4}>
            <TextField name="name" label="Pavadinimas" />
            <LegacyEditor name="description" label="Aprašymas" />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={updating}
            type="submit"
            variant="contained"
            startIcon={<PlaylistAddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default AddWhatToSeeBlockDialog;
